import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { useEffect, useMemo, useState } from 'react';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Price = ({
  currency,
  simplifyCurrency,
  value,
  children
}) => {
  const {
    productType
  } = useDetailsPageContext();
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "baseline",
    gap: "100",
    children: [/*#__PURE__*/_jsxs(Text, {
      marginBlockEnd: "0",
      typography: "heading-600",
      children: [productType === 'APP' && /*#__PURE__*/_jsx(FormattedIntlCurrency, {
        currencyCode: currency,
        simplifyCurrency: simplifyCurrency,
        value: value
      }), productType === 'AGENT' && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.pricingCard.plan.pricing.credits",
        options: {
          value
        }
      })]
    }), children ? /*#__PURE__*/_jsx(Text, {
      as: "small",
      children: children
    }) : null]
  });
};
export function PricingPlan({
  currency,
  pricing,
  onTrackInteraction
}) {
  const {
    productType
  } = useDetailsPageContext();
  const {
    pricingModel,
    pricingName,
    extraCharges,
    pricingDetail,
    pricingContactUsFrequency,
    pricingContactUsLink,
    pricingContactUsStartsAt,
    pricingFeatures,
    pricingMeteredCenticents,
    pricingMeteredUnit,
    pricingMonthlyCenticents,
    pricingTagline,
    pricingYearlyCenticents,
    pricingYearlyDiscountedCenticents
  } = pricing;

  // Extract planType from pricingModel array (V3 format)
  const planType = pricingModel && pricingModel.length > 0 ? pricingModel[0] : undefined;
  const [showCommitmentPricing, setShowCommitmentPricing] = useState(true);
  useEffect(() => {
    setShowCommitmentPricing(true);
  }, [pricing]);
  const renderedPrice = useMemo(() => {
    let value;
    switch (planType) {
      case 'FREE':
        {
          value = 0;
          break;
        }
      case 'AGENT':
        {
          if (typeof pricingMonthlyCenticents === 'number') {
            value = pricingMonthlyCenticents / 10000;
          }
          break;
        }
      case 'MONTHLY':
        {
          if (showCommitmentPricing && pricingYearlyDiscountedCenticents) {
            // Convert centicents to dollars, then divide by 12 for monthly
            value = pricingYearlyDiscountedCenticents / 10000 / 12;
          } else {
            // Convert centicents to dollars
            value = (pricingMonthlyCenticents || 0) / 10000;
          }
          break;
        }
      case 'YEARLY':
        {
          if (typeof pricingYearlyCenticents === 'number') {
            value = pricingYearlyCenticents / 10000;
          }
          break;
        }
      case 'METERED':
        {
          if (typeof pricingMeteredCenticents === 'number') {
            value = pricingMeteredCenticents / 10000;
          }
          break;
        }
      case 'CONTACT_US':
        {
          if (typeof pricingContactUsStartsAt === 'number' && pricingContactUsStartsAt > 0) {
            // CONTACT_US pricing can start at a specific amount
            value = pricingContactUsStartsAt / 10000;
          }
          break;
        }
      default:
        {
          // Unknown type or no value provided; do not render pricing text
          value = null;
        }
    }
    let pricingPlanKey;
    if (planType === 'CONTACT_US') {
      pricingPlanKey = (pricingContactUsFrequency === null || pricingContactUsFrequency === void 0 ? void 0 : pricingContactUsFrequency[0]) || 'ONE_TIME';
    } else if (productType === 'AGENT') {
      pricingPlanKey = 'AGENT';
    } else {
      pricingPlanKey = planType;
    }
    return typeof value === 'number' ? /*#__PURE__*/_jsxs(Flex, {
      alignItems: "baseline",
      gap: "100",
      children: [planType === 'CONTACT_US' && /*#__PURE__*/_jsx(Text, {
        as: "small",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.pricingCard.plan.startsAt"
        })
      }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: `detailsPage.pricingCard.plan.pricing.${pricingPlanKey}_jsx`,
        options: {
          currency,
          simplifyCurrency: true,
          value,
          meterUnit: pricingMeteredUnit
        },
        elements: {
          Price,
          Text
        }
      })]
    }) : null;
  }, [pricingMonthlyCenticents, pricingYearlyCenticents, pricingYearlyDiscountedCenticents, currency, pricingMeteredUnit, planType, productType, showCommitmentPricing, pricingMeteredCenticents, pricingContactUsStartsAt, pricingContactUsFrequency]);
  return /*#__PURE__*/_jsxs(CardWrapper, {
    children: [/*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [/*#__PURE__*/_jsxs(Flex, {
          justifyContent: "space-between",
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(Text, {
            children: pricingName
          }), planType === 'MONTHLY' && pricingYearlyDiscountedCenticents ? /*#__PURE__*/_jsxs(ToggleGroup, {
            name: "commitment-pricing-toggle",
            children: [/*#__PURE__*/_jsx(RadioInput, {
              checked: showCommitmentPricing,
              inline: true,
              size: "small",
              onClick: () => {
                setShowCommitmentPricing(true);
                onTrackInteraction({
                  action: 'clicked yearly pricing toggle'
                });
              },
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.pricingCard.plan.commitmentPricingToggleLabels.yearly"
              })
            }), /*#__PURE__*/_jsx(RadioInput, {
              checked: !showCommitmentPricing,
              inline: true,
              size: "small",
              onClick: () => {
                setShowCommitmentPricing(false);
                onTrackInteraction({
                  action: 'clicked monthly pricing toggle'
                });
              },
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.pricingCard.plan.commitmentPricingToggleLabels.monthly"
              })
            })]
          }) : null]
        }), renderedPrice, extraCharges ? /*#__PURE__*/_jsx(Text, {
          as: "small",
          children: extraCharges
        }) : null, pricingTagline ? /*#__PURE__*/_jsx(Text, {
          children: pricingTagline
        }) : null, Array.isArray(pricingFeatures) ? /*#__PURE__*/_jsx(List, {
          children: pricingFeatures.filter(feature => feature && feature.trim()).map((feature, index) => /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(MediaObject, {
              marginBlockEnd: "200",
              align: "center",
              spacing: "none",
              itemLeft: /*#__PURE__*/_jsx(IconHolder, {
                icon: SuccessIcon,
                color: "fill-positive-default"
              }),
              children: /*#__PURE__*/_jsx(Text, {
                typography: "detail-200",
                children: feature
              })
            })
          }, `pricing-feature-${index}`))
        }) : null, pricingDetail ? /*#__PURE__*/_jsx(Text, {
          as: "small",
          children: pricingDetail
        }) : null]
      })
    }), planType === 'CONTACT_US' && pricingContactUsLink ? /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(Button, {
        external: true,
        href: pricingContactUsLink,
        size: "sm",
        use: "tertiary-light",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.pricingCard.plan.contactUs"
        })
      })
    }) : null]
  });
}